import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, FormField, Input } from '../../components/ui/index.js';

interface Props {
  /** Performs the change; should reject (axios throw) on failure. */
  submit: (currentPassword: string, newPassword: string) => Promise<void>;
}

/**
 * Self-service "change my password" form. Reused by both the staff profile page
 * and the platform-admin page — the only difference is which endpoint `submit`
 * calls. Verifies the new password matches its confirmation and surfaces the
 * server's "current password incorrect" / "too weak" errors.
 */
export function ChangePasswordCard({ submit }: Props): JSX.Element {
  const { t } = useTranslation('settings');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setDone(false);
    if (next !== confirm) {
      setError(t('password.mismatch', { defaultValue: 'The new passwords do not match.' }));
      return;
    }
    setSaving(true);
    try {
      await submit(current, next);
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      const res = (err as { response?: { status?: number; data?: { cause?: { code?: string } } } }).response;
      const code = res?.data?.cause?.code;
      setError(
        code === 'invalid_current_password'
          ? t('password.wrongCurrent', { defaultValue: 'Your current password is incorrect.' })
          : res?.status === 422
            ? t('password.weak', {
                defaultValue:
                  'New password must be at least 10 characters with an uppercase letter, a lowercase letter, and a digit.',
              })
            : t('password.error', { defaultValue: 'Could not change the password. Please try again.' }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <h2 className="mb-1 text-lg font-semibold">
        {t('password.title', { defaultValue: 'Change password' })}
      </h2>
      <p className="mb-4 text-sm text-fg-muted">
        {t('password.hint', {
          defaultValue:
            'At least 10 characters, with an uppercase letter, a lowercase letter, and a digit.',
        })}
      </p>
      <form className="flex max-w-md flex-col gap-4" onSubmit={onSubmit}>
        <FormField id="currentPassword" label={t('password.current', { defaultValue: 'Current password' })}>
          <Input
            id="currentPassword"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </FormField>
        <FormField id="newPassword" label={t('password.new', { defaultValue: 'New password' })}>
          <Input
            id="newPassword"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </FormField>
        <FormField id="confirmPassword" label={t('password.confirm', { defaultValue: 'Confirm new password' })}>
          <Input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </FormField>
        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        {done && (
          <p className="text-sm text-success" role="status">
            {t('password.saved', { defaultValue: 'Password changed.' })}
          </p>
        )}
        <Button
          type="submit"
          loading={saving}
          disabled={!current || !next || !confirm}
          className="self-start"
        >
          {t('password.submit', { defaultValue: 'Change password' })}
        </Button>
      </form>
    </Card>
  );
}
